import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiGet, apiPost } from '../api/client';
import cantalLogo from '../assets/cantal.png';

interface AdminUserDto {
  id: string;
  email: string;
  displayName: string;
  role: string;
  status: string;
  createdAt: string;
  hasPassword: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  Approved: 'Approuvé',
  Pending: 'En attente',
  Rejected: 'Refusé',
  Suspended: 'Suspendu',
};

const STATUS_COLOR: Record<string, { bg: string; color: string }> = {
  Approved:  { bg: '#dcfce7', color: '#15803d' },
  Pending:   { bg: '#fef9c3', color: '#854d0e' },
  Rejected:  { bg: '#fee2e2', color: '#b91c1c' },
  Suspended: { bg: '#f1f5f9', color: '#64748b' },
};

const inputStyle: React.CSSProperties = {
  padding: '0.55rem 0.875rem',
  fontSize: '0.875rem',
  borderRadius: '8px',
  border: '1.5px solid #d1d5db',
  flex: 1,
  minWidth: '160px',
  outline: 'none',
  color: '#111827',
};

export default function AdminPage(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const [newEmail, setNewEmail] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiGet<AdminUserDto[]>('/admin/users');
      setUsers(data);
    } catch {
      setError('Impossible de charger les utilisateurs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const action = async (id: string, endpoint: string) => {
    setPending(p => ({ ...p, [id]: true }));
    try {
      await apiPost(`/admin/users/${id}/${endpoint}`);
      await load();
    } catch {
      setError('Action échouée.');
    } finally {
      setPending(p => ({ ...p, [id]: false }));
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    setCreateSuccess(null);
    setCreating(true);
    try {
      await apiPost('/admin/users', { email: newEmail, displayName: newDisplayName });
      setCreateSuccess(`Compte créé pour ${newEmail}. L'utilisateur devra définir son mot de passe lors de sa première connexion.`);
      setNewEmail('');
      setNewDisplayName('');
      await load();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Erreur lors de la création.');
    } finally {
      setCreating(false);
    }
  };

  const pendingCount = users.filter(u => u.status === 'Pending').length;

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <nav style={{
        background: '#1e3a5f',
        color: '#fff',
        padding: '0 1.75rem',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
          <button onClick={() => navigate('/')} style={{ background: 'transparent', border: 'none', color: '#a8c4e0', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, padding: 0 }}>
            ← Calendrier
          </button>
          <span style={{ color: '#2d5a8e', fontSize: '1rem' }}>|</span>
          <img src={cantalLogo} alt="Pierroinfo" style={{ height: '38px', width: 'auto' }} />
          <span style={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>Pierroinfo</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', fontSize: '0.875rem' }}>
          <span style={{ color: '#a8c4e0', fontWeight: 500 }}>{user?.displayName}</span>
          <button onClick={() => void logout()} style={{ background: 'transparent', border: '1px solid #a8c4e0', color: '#a8c4e0', padding: '0.3rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}>
            Déconnexion
          </button>
        </div>
      </nav>

      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1.25rem' }}>

        {/* Create user card */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <h2 style={{ fontSize: '0.9rem', fontWeight: 700, margin: '0 0 1rem', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Ajouter un utilisateur
          </h2>
          <form onSubmit={(e) => void handleCreateUser(e)} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <input type="text" placeholder="Nom affiché" value={newDisplayName} onChange={(e) => setNewDisplayName(e.target.value)} required style={inputStyle} />
            <input type="email" placeholder="Email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required style={inputStyle} />
            <button
              type="submit"
              disabled={creating}
              style={{
                background: creating ? '#e2e8f0' : '#1e3a5f',
                color: creating ? '#94a3b8' : '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '0.55rem 1.1rem',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: creating ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                letterSpacing: '0.01em',
              }}
            >
              {creating ? 'Création…' : 'Créer le compte'}
            </button>
          </form>
          {createError && <p style={{ color: '#dc2626', margin: '0.875rem 0 0', fontSize: '0.85rem' }}>{createError}</p>}
          {createSuccess && (
            <p style={{ color: '#15803d', margin: '0.875rem 0 0', fontSize: '0.85rem', background: '#f0fdf4', borderRadius: '6px', padding: '0.6rem 0.875rem', border: '1px solid #bbf7d0' }}>
              {createSuccess}
            </p>
          )}
        </div>

        {/* Users list */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1rem' }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0, color: '#0f172a' }}>Gestion des comptes</h1>
          {pendingCount > 0 && (
            <span style={{ background: '#f59e0b', color: '#fff', borderRadius: '99px', padding: '0.15rem 0.7rem', fontSize: '0.78rem', fontWeight: 700 }}>
              {pendingCount} en attente
            </span>
          )}
        </div>

        {error && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.875rem', border: '1px solid #fecaca' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>Chargement…</div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '0.75rem 1.25rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Utilisateur</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Rôle</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Statut</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Inscrit le</th>
                  <th style={{ padding: '0.75rem 1.25rem', textAlign: 'right', fontWeight: 600, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const colors = STATUS_COLOR[u.status] ?? { bg: '#f1f5f9', color: '#64748b' };
                  const isSelf = u.id === user?.id;
                  const busy = !!pending[u.id];
                  return (
                    <UserRow key={u.id} u={u} colors={colors} isSelf={isSelf} busy={busy} onAction={(ep) => void action(u.id, ep)} />
                  );
                })}
              </tbody>
            </table>
            {users.length === 0 && (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>Aucun utilisateur</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function UserRow({ u, colors, isSelf, busy, onAction }: {
  u: AdminUserDto;
  colors: { bg: string; color: string };
  isSelf: boolean;
  busy: boolean;
  onAction: (ep: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ borderBottom: '1px solid #f1f5f9', background: hovered ? '#fafbfc' : '#fff', transition: 'background 0.1s' }}
    >
      <td style={{ padding: '0.875rem 1.25rem' }}>
        <div style={{ fontWeight: 600, color: '#111827', marginBottom: '0.1rem' }}>{u.displayName}</div>
        <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{u.email}</div>
        {!u.hasPassword && (
          <span style={{ display: 'inline-block', marginTop: '0.25rem', background: '#fef3c7', color: '#92400e', borderRadius: '99px', padding: '0.1rem 0.55rem', fontSize: '0.72rem', fontWeight: 600 }}>
            Mot de passe non défini
          </span>
        )}
      </td>
      <td style={{ padding: '0.875rem 1rem', color: u.role === 'Admin' ? '#7c3aed' : '#475569', fontWeight: u.role === 'Admin' ? 700 : 400 }}>
        {u.role === 'Admin' ? 'Admin' : 'Commentateur'}
      </td>
      <td style={{ padding: '0.875rem 1rem' }}>
        <span style={{ background: colors.bg, color: colors.color, borderRadius: '99px', padding: '0.2rem 0.65rem', fontSize: '0.78rem', fontWeight: 600 }}>
          {STATUS_LABEL[u.status] ?? u.status}
        </span>
      </td>
      <td style={{ padding: '0.875rem 1rem', color: '#9ca3af', fontSize: '0.82rem' }}>
        {new Date(u.createdAt).toLocaleDateString('fr-FR')}
      </td>
      <td style={{ padding: '0.875rem 1.25rem', textAlign: 'right' }}>
        {!isSelf ? (
          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {u.status === 'Pending' && (
              <>
                <ActionBtn label="Approuver" color="#16a34a" busy={busy} onClick={() => onAction('approve')} />
                <ActionBtn label="Refuser" color="#dc2626" busy={busy} onClick={() => onAction('reject')} />
              </>
            )}
            {u.status === 'Approved' && (
              <ActionBtn label="Suspendre" color="#d97706" busy={busy} onClick={() => onAction('suspend')} />
            )}
            {(u.status === 'Rejected' || u.status === 'Suspended') && (
              <ActionBtn label="Réactiver" color="#2563eb" busy={busy} onClick={() => onAction('approve')} />
            )}
          </div>
        ) : (
          <span style={{ color: '#cbd5e1', fontSize: '0.78rem', fontStyle: 'italic' }}>Vous</span>
        )}
      </td>
    </tr>
  );
}

function ActionBtn({ label, color, busy, onClick }: { label: string; color: string; busy: boolean; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={busy}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: busy ? '#e2e8f0' : hovered ? `${color}dd` : color,
        color: busy ? '#94a3b8' : '#fff',
        border: 'none',
        borderRadius: '6px',
        padding: '0.28rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 600,
        cursor: busy ? 'not-allowed' : 'pointer',
        transition: 'background 0.1s',
      }}
    >
      {label}
    </button>
  );
}
